import { arraysAreSame, setsAreSame } from './array';

/**
 * Returns a copy of the the supplied object including only properties
 * with values that are not `undefined`.
 *
 * @param obj The object to remove undefined properties from
 */
export function removeUndefinedProperties<T extends { [key in keyof T]: unknown }>(
    obj: T,
): Partial<T> {
    return Object.entries(obj).reduce<Partial<T>>((result, [propName, value]) => {
        if (value === undefined) {
            return result;
        }

        result[propName as keyof T] = value as T[keyof T];
        return result;
    }, {});
}

/**
 * Compare two objects to see if they contain the same keys & values.
 *
 * @param obj1 The first object
 * @param obj2 The second object
 * @returns `true` if the two objects are the same, `false` otherwise.
 * @throws Errors if either of the provided arguments are not objects.
 */
export function objectsAreSame<T>(obj1: NonNullable<T>, obj2: NonNullable<T>) {
    if (typeof obj1 !== 'object') {
        throw new Error('obj1 is not an object');
    }
    if (typeof obj2 !== 'object') {
        throw new Error('obj2 is not an object');
    }
    if (obj1 === obj2) {
        return true;
    }
    const keys = [...Object.keys(obj1), ...Object.keys(obj2)] as (keyof T)[];
    return keys.every((key) => obj1[key] === obj2[key]);
}

/**
 * Compare two objects to see if they contain the same keys & values.
 *
 * @param obj1 The first object
 * @param obj2 The second object
 * @param comparer The function to use to compare the objects' props.
 * @returns `true` if the two objects are the same, `false` otherwise.
 * @throws Errors if either of the provided arguments are not objects.
 */
export function mapsAreSame<T, V>(
    obj1: NonNullable<{ [key in keyof T]: V }>,
    obj2: NonNullable<{ [key in keyof T]: V }>,
    propComparer: (a: V, b: V) => boolean,
) {
    if (typeof obj1 !== 'object') {
        throw new Error('obj1 is not an object');
    }
    if (typeof obj2 !== 'object') {
        throw new Error('obj2 is not an object');
    }
    if (obj1 === obj2) {
        return true;
    }
    const keys = [...Object.keys(obj1), ...Object.keys(obj2)] as (keyof T)[];
    return keys.every((key) => propComparer(obj1[key], obj2[key]));
}

/**
 * Returns a new object containing only the specified properties.
 *
 * @param obj the starting objects
 * @param properties the properties to copy (shallow) from the object.
 */
export function getPartialObject<
    TObject extends Record<string, unknown>,
    TKeys extends (keyof TObject)[],
>(
    obj: TObject,
    properties: TKeys,
): {
    [TProperty in TKeys[0]]: TObject[TProperty];
} {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return properties.reduce<any>((result, key) => {
        if (key in obj) {
            result[key] = obj[key];
        }
        return result;
    }, {});
}

/**
 * Returns a list of object property names.
 */
export function getPropertyNames<TObject extends Record<string, unknown>>(
    obj: TObject,
): (keyof TObject)[] {
    return Object.keys(obj);
}

export const hasDefinedValues = (obj: Record<string, unknown>) => {
    const vals = Object.values(obj);
    if (vals.length === 0) {
        return false;
    }
    return vals.some((val) => val);
};

type Values<T> = T[keyof T];
type Entries<T> = [keyof T, Values<T>][];

/**
 * `Object.entries()` but preserving key type
 *
 * it should be noted that `objectEntries` is an implicit assertion that _only_
 * keys defined on `T` exist, which is technically a variety of unsafe (bc TS
 * only verifies that `obj` satisfies `T` (whatever `T` was in the calling
 * context). It may contain more than that, which would make the return value of
 * `objectEntries` incorrectly typed)
 */
export function objectEntries<T extends object>(obj: T): Entries<T> {
    return Object.entries(obj) as Entries<T>;
}

/**
 * performs a deep object comparison wherein objects with the same keys and
 * values are considered equal. Array elements with the same values, in any
 * order, are considered equal. These array elements are compared by reference,
 * not using any sort of deep comparison.
 */
export function deepObjectEquals(a: unknown, b: unknown, maxDepth: number): boolean {
    if (maxDepth === 0) {
        return false;
    }
    if (a === b) {
        return true;
    }
    if (a === null || b === null) {
        // one or the other but not both were null
        return false;
    }

    // array comparison
    if (Array.isArray(a) && Array.isArray(b)) {
        return arraysAreSame(a, b);
    }

    // object comparison
    if (typeof a === 'object' && typeof b === 'object') {
        return (
            setsAreSame(new Set(Object.keys(a)), new Set(Object.keys(b))) &&
            Object.keys(a).every((k) =>
                deepObjectEquals(
                    a[k as keyof typeof a],
                    b[k as keyof typeof a],
                    maxDepth - 1,
                ),
            )
        );
    }

    return a === b;
}
